import { z } from 'zod';

/**
 * Boundary schema for the Typesense multi-search response — parsed, never
 * `as`-cast (external payload, heavy typesense-js generics). `results[i]`
 * aligns positionally with the i-th search we sent.
 */
/**
 * Per-field highlight Typesense returns for a hit. `snippet` embeds literal
 * `<mark>`/`</mark>` tags around the matched tokens — rendered by splitting on
 * those tags into React text nodes, NEVER via `dangerouslySetInnerHTML`. Shape
 * is tolerant: an odd/absent highlight parses to `{}` so a hit never fails.
 */
const highlightFieldSchema = z
  .object({ snippet: z.string().optional(), matched_tokens: z.unknown().optional() })
  .catch({});

const searchHitSchema = z.object({
  document: z.record(z.string(), z.unknown()),
  /** Relevance score; lets us pick a single cross-collection "Best match". */
  text_match: z.number().optional(),
  /** `fieldName → { snippet }`; which field matched drives the leading slot. */
  highlight: z.record(z.string(), highlightFieldSchema).catch({}),
});

/** `facet_by` count buckets — powers the scoped-tab status sub-facets. */
const facetCountSchema = z.object({
  field_name: z.string(),
  counts: z.array(z.object({ value: z.string(), count: z.number() })).catch([]),
});

const searchResultSchema = z.object({
  /** Total matches in the collection (drives the facet count, not just the page). */
  found: z.number().catch(0),
  hits: z.array(searchHitSchema).catch([]),
  facet_counts: z.array(facetCountSchema).catch([]),
  /** Per-leg failure: multi_search returns 200 with `{ error, code }` when a
   *  collection is missing/misconfigured (api/src/lib/typesense/search.ts:79-89).
   *  Without these members that error parses cleanly into an empty group — a
   *  failed leg becomes indistinguishable from a genuine zero-match. */
  error: z.string().optional(),
  code: z.number().optional(),
});

export const multiSearchResponseSchema = z.object({ results: z.array(searchResultSchema).catch([]) });

export type MultiSearchResponse = z.infer<typeof multiSearchResponseSchema>;
export type SearchResult = z.infer<typeof searchResultSchema>;
export type SearchHit = z.infer<typeof searchHitSchema>;
/** `fieldName → { snippet }` for one hit; keys are Typesense document fields. */
export type SearchHighlight = z.infer<typeof searchHitSchema>['highlight'];
